/**
 * メール詳細画面の企業グループピッカー (ISSUE-803 Phase 1C-b)。
 *
 * 送信元ドメインから company を解決し、ワンクリックで企業グループに付与する。
 * 未登録ドメインの場合は「会社として登録 → 企業グループに付与」を 1 フローで完結させる。
 */
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { type Company, useCompanyMutations } from "hooks/useCompaniesApi";
import {
  useCompanyGroupMutations,
  useCompanyGroupsApi,
} from "hooks/useCompanyGroupsApi";
import { Tag, X } from "lucide-react";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { companyGroupColorClass } from "utils/company-group-colors";
import { toastError } from "utils/toast-utils";

/**
 * email アドレスから lowercase の domain を抜き出す。
 * @param {string} email email アドレス
 * @return {string | null} domain または null
 */
function extractDomain(email: string): string | null {
  if (!email || !email.includes("@")) return null;
  const idx = email.lastIndexOf("@");
  const domain = email
    .slice(idx + 1)
    .trim()
    .toLowerCase();
  return domain || null;
}

type Props = {
  fromAddress: string;
};

/**
 * 企業グループピッカー本体。送信者 email からドメインを抽出し、
 * 該当 company の所属グループを表示しつつワンクリック toggle 可能にする。
 * @param {Props} props from address
 * @return {JSX.Element} popover トリガとパネル
 */
export const CompanyGroupPicker: FC<Props> = ({ fromAddress }) => {
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement | null>(null);

  const domain = extractDomain(fromAddress);

  const { data: groupsData, loading: groupsLoading } = useCompanyGroupsApi({
    skip: !open,
  });
  const {
    byDomains,
    createCompany,
    loading: companyMutating,
  } = useCompanyMutations();
  const {
    addCompany,
    removeCompany,
    loading: groupMutating,
  } = useCompanyGroupMutations();

  const [resolved, setResolved] = useState<{
    company: Company | null;
    groupIds: Set<string>;
  } | null>(null);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [createName, setCreateName] = useState("");
  const [creating, setCreating] = useState(false);

  // 開いた時にドメインから company 解決
  const refresh = useCallback(async () => {
    if (!domain) return;
    setResolveLoading(true);
    try {
      const res = await byDomains([domain]);
      const item = res.items.find(i => i.domain === domain);
      if (item) {
        setResolved({
          company: item.company,
          groupIds: new Set(item.groups.map(g => g.id)),
        });
      } else {
        setResolved({ company: null, groupIds: new Set() });
      }
    } catch {
      setResolved({ company: null, groupIds: new Set() });
    } finally {
      setResolveLoading(false);
    }
  }, [domain, byDomains]);

  useEffect(() => {
    if (open) {
      refresh();
    }
  }, [open, refresh]);

  // 外側クリックで閉じる
  useEffect(() => {
    if (!open) return;
    const onClick = (ev: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!domain) {
    return null; // ドメイン抽出不能なら表示しない
  }

  const handleToggleGroup = async (groupId: string) => {
    if (!resolved?.company) return;
    const isMember = resolved.groupIds.has(groupId);
    try {
      if (isMember) {
        await removeCompany(groupId, resolved.company.id);
      } else {
        await addCompany(groupId, resolved.company.id);
      }
      await refresh();
    } catch (e) {
      toastError(
        `更新に失敗しました: ${e instanceof Error ? e.message : "不明"}`,
      );
    }
  };

  const handleCreateCompany = async () => {
    if (!createName.trim()) return;
    setCreating(true);
    try {
      await createCompany({
        name: createName.trim(),
        primaryDomain: domain,
      });
      setCreateName("");
      await refresh();
    } catch (e) {
      toastError(
        `会社登録に失敗しました: ${e instanceof Error ? e.message : "不明"}`,
      );
    } finally {
      setCreating(false);
    }
  };

  const groups = groupsData?.items ?? [];
  const memberIds = resolved?.groupIds ?? new Set<string>();

  return (
    <div className="relative inline-block" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-slate-100 hover:bg-slate-200 text-slate-700"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="企業グループに付与"
      >
        <Tag className="w-3 h-3" />
        企業グループ
      </button>

      {open && (
        <div
          role="dialog"
          className="absolute z-50 mt-1 w-80 rounded-lg border border-slate-200 bg-white shadow-lg p-3"
        >
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-xs text-base-content/60">送信元ドメイン</div>
              <code className="text-sm font-medium">{domain}</code>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-base-content/60 hover:text-base-content"
              aria-label="閉じる"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {resolveLoading ? (
            <div className="flex justify-center py-4">
              <Loading size="sm" />
            </div>
          ) : !resolved?.company ? (
            <div className="border-t border-slate-200 pt-3">
              <div className="text-xs text-base-content/60 mb-2">
                このドメインはまだ会社として登録されていません
              </div>
              <input
                type="text"
                value={createName}
                onChange={e => setCreateName(e.target.value)}
                placeholder="会社名を入力"
                className="input input-bordered input-sm w-full mb-2"
              />
              <Button
                type="button"
                size="sm"
                width="full"
                onClick={handleCreateCompany}
                disabled={!createName.trim() || creating || companyMutating}
              >
                会社として登録
              </Button>
            </div>
          ) : (
            <div className="border-t border-slate-200 pt-3">
              <div className="text-xs text-base-content/60 mb-1">会社</div>
              <div className="text-sm font-medium mb-3">
                {resolved.company.name}
              </div>
              <div className="text-xs text-base-content/60 mb-2">
                企業グループ
              </div>
              {groupsLoading ? (
                <Loading size="sm" />
              ) : groups.length === 0 ? (
                <p className="text-xs text-base-content/60">
                  企業グループがまだ作成されていません
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {groups.map(g => {
                    const isMember = memberIds.has(g.id);
                    return (
                      <button
                        type="button"
                        key={g.id}
                        onClick={() => handleToggleGroup(g.id)}
                        disabled={groupMutating}
                        className={`px-2 py-1 rounded text-xs ${companyGroupColorClass(g.color)} ${
                          isMember
                            ? "ring-2 ring-offset-1 ring-slate-700"
                            : "opacity-60 hover:opacity-100"
                        }`}
                        aria-pressed={isMember}
                      >
                        {isMember ? "✓ " : "+ "}
                        {g.isBlacklisted && (
                          <span className="mr-1">
                            <StatusBadge size="xs" tone="tag-danger">
                              ブラックリスト
                            </StatusBadge>
                          </span>
                        )}
                        {g.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
